import { useMemo, useRef, useState } from "react";
import { api } from "../../api.js";
import { SuggestInput, loadCodes } from "./Suggest.jsx";
import { RATES, calcRow, kopUi, decText } from "../../../../lib/pn/calc.js";
import { UNITS, newRow, SERVICE_UNIT, DEFAULT_RATE } from "../../../../lib/pn/model.js";
import { splitPaste, guessMapping, buildPasteRows, normalizeMapping, importAllowed, PASTE_FIELDS } from "../../../../lib/pn/paste.js";

// Товари та послуги: рядки-картки (на телефоні — одна колонка). Сума рядка рахується тим самим
// ядром, що й XML (lib/pn/calc.js). Ставка нового рядка — стандартна 20% (DEFAULT_RATE), видно в полі;
// з попереднього продажу того самого товару — його ставка, і тоді видно звідки
const fieldId = (i, f) => `pn-rows-${i}-${f}`;
const vatUi = (micro) => kopUi((micro + 5_000n) / 10_000n);

// Поле помилки на екрані — за шляхом перевірки (rows.3.name → pn-rows-3-name), як fid у PnWizard
const elOf = (path) => document.getElementById(`pn-${path.replaceAll(".", "-")}`);
const byPosition = (els) => els.sort((x, y) => (x.compareDocumentPosition(y) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));

export default function PnRows({ companyId, doc, update, errorsFor, rowErrors = [], showErrors }) {
  const [paste, setPaste] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [added, setAdded] = useState(null);
  const pasteBtn = useRef(null);
  const setRow = (i, patch) => update((d) => ({ ...d, rows: d.rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const remove = (i) => { setSelected((s) => { const n = new Set(s); n.delete(doc.rows[i]?.id); return n; }); update((d) => ({ ...d, rows: d.rows.length > 1 ? d.rows.filter((_, j) => j !== i) : [newRow()] })); };
  const add = () => update((d) => ({ ...d, rows: [...d.rows, newRow()] }));
  // Наступна помилка після тієї, де зараз фокус (по колу) — виправляєте адресно, решта введеного лишається
  // Остання відвідана помилка — у ref: натискання кнопки забирає фокус із поля
  const lastErr = useRef(null);
  const nextError = () => {
    const els = byPosition(rowErrors.map((x) => elOf(x.path)).filter(Boolean));
    if (!els.length) return;
    const at = els.findIndex((el) => el === document.activeElement || el.id === lastErr.current);
    const el = els[(at + 1) % els.length];
    lastErr.current = el.id;
    el.focus(); el.scrollIntoView({ block: "center" });
  };
  const addPasted = (rows, problems) => {
    const start = doc.rows.filter((r) => r.name.trim()).length;
    update((d) => ({ ...d, rows: [...d.rows.filter((r) => r.name.trim()), ...rows] }));
    setPaste(false);
    setAdded({ n: rows.length, problems });
    if (problems) showErrors?.();
    setTimeout(() => document.getElementById(fieldId(start, "name"))?.focus(), 0);
  };
  const loadNomen = (q) => api(`/api/companies/${companyId}/pn/nomenclature?q=${encodeURIComponent(q)}`).then((r) => r.items);
  const gross = doc.priceMode === "gross";

  const rowsErr = errorsFor("rows");
  return (
    <div className="pn-rows" id="pn-rows" tabIndex={-1} aria-describedby={rowsErr ? "pn-rows-err" : undefined}>
      {rowsErr && <p className="error" id="pn-rows-err" role="alert">{rowsErr}</p>}
      <div className="pn-rows-head">
        <fieldset className="seg" aria-label="Як вказуєте ціни">
          <legend className="sr-only">Ціни вказую</legend>
          {[["net", "Ціни без ПДВ"], ["gross", "Ціни з ПДВ"]].map(([k, l]) => (
            <label key={k} className={doc.priceMode === k ? "on" : ""}>
              <input type="radio" name="priceMode" checked={doc.priceMode === k} onChange={() => update((d) => ({ ...d, priceMode: k }))} />{l}
            </label>
          ))}
        </fieldset>
        <span className="muted small">{gross ? "Суму з ПДВ збережемо до копійки, базу й ПДВ виділимо з неї." : "ПДВ нарахуємо зверху на ціну."}</span>
      </div>
      {rowErrors.length > 0 && (
        <div className="pn-errnav" role="status">
          <span>Помилок у рядках: <b>{rowErrors.length}</b></span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={nextError}>До наступної помилки ↓</button>
        </div>
      )}
      {added && <p className="pn-src" role="status">Додано рядків: {added.n}{added.problems ? `, з них з помилками: ${added.problems} — вони позначені нижче` : ""}.</p>}
      {doc.rows.length > 1 && <BulkRate rows={doc.rows} selected={selected} setSelected={setSelected} update={update} />}

      {doc.rows.map((r, i) => {
        const c = r.rate in RATES ? calcRow(r, doc.priceMode) : null;
        const service = r.type === "service";
        const err = (f) => errorsFor(`rows.${i}.${f}`);
        const E = ({ f }) => (err(f) ? <span className="pn-err" id={`${fieldId(i, f)}-err`}>{err(f)}</span> : null);
        const aria = (f) => ({ id: fieldId(i, f), "aria-invalid": Boolean(err(f)) || undefined, "aria-describedby": err(f) ? `${fieldId(i, f)}-err` : undefined });
        return (
          <section key={r.id} className="pn-row" aria-label={`Рядок ${i + 1}`}>
            <div className="pn-row-top">
              {doc.rows.length > 1 && <input type="checkbox" className="pn-row-sel" aria-label={`Вибрати рядок ${i + 1}`} checked={selected.has(r.id)}
                onChange={(e) => setSelected((s) => { const n = new Set(s); e.target.checked ? n.add(r.id) : n.delete(r.id); return n; })} />}
              <span className="pn-row-n" aria-hidden="true">{i + 1}</span>
              <label className="field pn-name">Назва товару чи послуги
                <SuggestInput id={fieldId(i, "name")} value={r.name} label={`Рядок ${i + 1}: назва`} invalid={Boolean(err("name"))} describedBy={err("name") ? `${fieldId(i, "name")}-err` : undefined}
                  placeholder="Почніть вводити — підкажемо з ваших ПН і МД"
                  onChange={(v) => setRow(i, { name: v, source: null })} load={loadNomen}
                  onPick={(it) => setRow(i, {
                    name: it.name, type: it.service ? "service" : "goods",
                    ...(it.service ? { dkpp: it.code || "", unit: SERVICE_UNIT } : { uktzed: it.code || "", unit: it.unit || r.unit }),
                    ...(it.imported ? { imported: true } : {}),
                    ...(it.price ? { price: it.price, amountBy: "price" } : {}),
                    ...(it.rate && it.rate in RATES ? { rate: it.rate } : {}),
                    source: it.source.text,
                  })}
                  render={(it) => (<><span>{it.name}</span><span className="muted small">{[it.code, it.source.text, it.price && `ціна ${it.price}`, it.rate && `ставка ${it.rate}%`].filter(Boolean).join(" · ")}</span></>)} />
                <E f="name" />
                {r.source && <span className="pn-src">✓ {r.source}{r.rate && r.source.startsWith("продавали") ? " — ставку взято звідти, перевірте" : ""}</span>}
              </label>
              <fieldset className="seg seg-sm">
                <legend className="sr-only">Рядок {i + 1}: товар чи послуга</legend>
                {[["goods", "Товар"], ["service", "Послуга"]].map(([k, l]) => (
                  <label key={k} className={r.type === k ? "on" : ""}>
                    <input type="radio" name={`type-${r.id}`} checked={r.type === k}
                      onChange={() => setRow(i, k === "service" ? { type: k, imported: false, ownAgro: false, unit: SERVICE_UNIT } : { type: k, unit: r.unit === SERVICE_UNIT ? "шт" : r.unit })} />{l}
                  </label>
                ))}
              </fieldset>
              <button type="button" className="btn btn-ghost btn-sm pn-del" onClick={() => remove(i)} aria-label={`Видалити рядок ${i + 1}`}>✕</button>
            </div>

            <div className="pn-row-grid">
              <label className="field pn-code">{service ? "Код послуги (ДКПП)" : "Код товару (УКТ ЗЕД)"}
                <SuggestInput {...{ id: fieldId(i, service ? "dkpp" : "uktzed") }} value={service ? r.dkpp : r.uktzed}
                  label={`Рядок ${i + 1}: ${service ? "код ДКПП" : "код УКТ ЗЕД"}`} invalid={Boolean(err(service ? "dkpp" : "uktzed"))}
                  describedBy={err(service ? "dkpp" : "uktzed") ? `${fieldId(i, service ? "dkpp" : "uktzed")}-err` : undefined}
                  placeholder={service ? "43.21.10 або назва" : "8544499300 або назва"} minLength={3}
                  onChange={(v) => setRow(i, service ? { dkpp: v, codeNote: null } : { uktzed: v, codeNote: null })} load={loadCodes(service)}
                  onPick={(it) => setRow(i, { ...(service ? { dkpp: it.code } : { uktzed: it.code }), codeNote: it.title })}
                  render={(it) => (<><span className="mono-sm">{it.code}</span><span className="muted small">{it.title}</span></>)} />
                <E f={service ? "dkpp" : "uktzed"} />
                {r.codeNote && <span className="pn-src">Обрано: {r.codeNote}. Перевірте, що це ваш {service ? "вид послуги" : "товар"}.</span>}
              </label>
              <label className="field pn-unit">Одиниця
                <input {...aria("unit")} list="pn-units" value={r.unit} onChange={(e) => setRow(i, { unit: e.target.value })} />
                <E f="unit" />
              </label>
              <label className="field pn-qty">Кількість
                <input {...aria("qty")} inputMode="decimal" value={r.qty} onChange={(e) => setRow(i, { qty: e.target.value })} />
                <E f="qty" />
              </label>
              {r.amountBy === "sum" ? (
                <label className="field pn-price">Сума рядка {gross ? "з ПДВ" : "без ПДВ"}
                  <input {...aria("sum")} inputMode="decimal" value={r.sum} onChange={(e) => setRow(i, { sum: e.target.value })} />
                  <E f="sum" />
                  <button type="button" className="linkish" onClick={() => setRow(i, { amountBy: "price" })}>ввести ціну</button>
                </label>
              ) : (
                <label className="field pn-price">Ціна {gross ? "з ПДВ" : "без ПДВ"}
                  <input {...aria("price")} inputMode="decimal" value={r.price} onChange={(e) => setRow(i, { price: e.target.value })} />
                  <E f="price" />
                  <button type="button" className="linkish" onClick={() => setRow(i, { amountBy: "sum" })}>ввести суму рядка</button>
                </label>
              )}
              <label className="field pn-rate">Ставка ПДВ
                <select {...aria("rate")} value={r.rate} onChange={(e) => setRow(i, { rate: e.target.value })}>
                  <option value="">Оберіть</option>
                  {Object.entries(RATES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <E f="rate" />
              </label>
              {r.rate === "903" && (
                <label className="field pn-benefit">Код пільги
                  <input {...aria("benefit")} inputMode="numeric" maxLength={8} value={r.benefit} onChange={(e) => setRow(i, { benefit: e.target.value.replace(/\D/g, "") })} />
                  <E f="benefit" />
                </label>
              )}
              <div className="pn-row-sum" aria-live="polite">
                {c?.ok ? (<>
                  <b>{kopUi(c.base)}</b><span className="muted small">без ПДВ</span>
                  <span className="muted small">ПДВ {vatUi(c.vat)} · разом {kopUi(c.gross)}</span>
                  {(r.amountBy === "sum" || gross) && <span className="muted small">ціна без ПДВ {decText(c.price).replace(".", ",")}</span>}
                </>) : <span className="muted small">Сума з'явиться, коли вкажете кількість, ціну й ставку</span>}
              </div>
            </div>
            {!service && (
              <div className="pn-flags">
                <label className="check"><input type="checkbox" checked={r.imported} onChange={(e) => setRow(i, { imported: e.target.checked })} />
                  Товар ввезено в Україну (імпорт){r.imported ? " — потрібен повний код УКТ ЗЕД, 10 цифр" : ""}</label>
                <label className="check"><input type="checkbox" checked={r.ownAgro} onChange={(e) => setRow(i, { ownAgro: e.target.checked })} />
                  Власна сільськогосподарська продукція</label>
              </div>
            )}
          </section>
        );
      })}
      <datalist id="pn-units">{UNITS.map(([u, , t]) => <option key={u} value={u}>{t}</option>)}<option value={SERVICE_UNIT} /></datalist>
      <div className="actions">
        <button type="button" className="btn btn-secondary btn-sm" onClick={add}>+ Додати рядок</button>
        <button type="button" ref={pasteBtn} className="btn btn-secondary btn-sm" onClick={() => setPaste((v) => !v)} aria-expanded={paste}>Вставити з таблиці</button>
      </div>
      {paste && <PasteRows onDone={addPasted} onCancel={() => { setPaste(false); pasteBtn.current?.focus(); }} />}
    </div>
  );
}

// Групова ставка: явно для вибраних рядків. Різні вже вказані ставки мовчки не перезаписуємо — питаємо
function BulkRate({ rows, selected, setSelected, update }) {
  const [rate, setRate] = useState("");
  const [ask, setAsk] = useState(null);
  const [done, setDone] = useState(null);
  const ids = rows.map((r) => r.id), sel = ids.filter((id) => selected.has(id));
  const all = sel.length === ids.length;
  const apply = (onlyEmpty) => {
    const targets = rows.filter((r) => selected.has(r.id) && (!onlyEmpty || !r.rate));
    const set = new Set(targets.map((r) => r.id));
    update((d) => ({ ...d, rows: d.rows.map((r) => (set.has(r.id) ? { ...r, rate } : r)) }));
    setAsk(null); setDone({ n: targets.length, label: RATES[rate].label });
  };
  const start = () => {
    setDone(null);
    const other = rows.filter((r) => selected.has(r.id) && r.rate && r.rate !== rate).length;
    if (other) setAsk({ other }); else apply(false);
  };
  return (
    <div className="pn-bulk" role="group" aria-label="Ставка для кількох рядків">
      <label className="check"><input type="checkbox" checked={all} ref={(el) => { if (el) el.indeterminate = sel.length > 0 && !all; }}
        onChange={() => setSelected(all ? new Set() : new Set(ids))} />Вибрати всі ({ids.length})</label>
      <label className="pn-bulk-rate">Ставка для вибраних
        <select value={rate} onChange={(e) => { setRate(e.target.value); setAsk(null); setDone(null); }}>
          <option value="">Оберіть</option>
          {Object.entries(RATES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </label>
      <button type="button" className="btn btn-secondary btn-sm" disabled={!sel.length || !rate} onClick={start}>
        Застосувати{sel.length ? ` до ${sel.length}` : ""}</button>
      {!sel.length && <span className="muted small">Позначте рядки прапорцями або «Вибрати всі».</span>}
      {ask && (
        <div className="pn-bulk-ask" role="alert">
          <span>У {ask.other} з {sel.length} вибраних рядків уже інша ставка.</span>
          <button type="button" className="btn btn-sm" onClick={() => apply(false)}>Замінити в усіх {sel.length}</button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => apply(true)}>Лише там, де порожньо</button>
          <button type="button" className="linkish" onClick={() => setAsk(null)}>Скасувати</button>
        </div>
      )}
      {done && <span className="pn-ok small" role="status">Ставку {done.label} вказано в {done.n} рядк.</span>}
    </div>
  );
}

// Вставлення кількох рядків з Excel, Google Таблиць чи облікової програми: спершу попередній перегляд —
// які колонки що означають (можна змінити), які рядки додаються, які пропущено й чому, де помилки
function PasteRows({ onDone, onCancel }) {
  const [text, setText] = useState("");
  const [over, setOver] = useState(null);       // ручне зіставлення колонок
  const [header, setHeader] = useState(null);   // ручне «перший рядок — заголовки»
  const [defRate, setDefRate] = useState(DEFAULT_RATE);
  const lines = useMemo(() => splitPaste(text), [text]);
  const guess = useMemo(() => guessMapping(lines), [lines]);
  const first = lines.find((l) => l.cells.some(Boolean));
  const norm = normalizeMapping(over && over.length === guess.mapping.length ? over : guess.mapping);
  const map = { mapping: norm.mapping, headerLine: header === null ? guess.headerLine : header ? first?.line ?? null : null };
  const note = norm.note || guess.note;
  const res = useMemo(() => buildPasteRows(lines, map, { defaultRate: defRate }), [lines, map.mapping, map.headerLine, defRate]);
  const noRate = res.rows.filter((x) => x.rateFrom !== "column").length;
  const bad = res.rows.filter((x) => x.problems.length).length;
  const hasName = map.mapping.includes("name");
  const setCol = (i, k) => setOver(map.mapping.map((x, j) => (j === i ? k : x === k && k !== "skip" ? "skip" : x)));
  return (
    <div className="card pn-paste" onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }}>
      <label className="field">Скопіюйте рядки з таблиці й вставте сюди
        <span className="field-hint">Можна разом із рядком заголовків. Колонки впізнаємо за назвами або за порядком «назва · код · одиниця · кількість · ціна»;
          ставку ПДВ можна додати окремою колонкою. Нижче кожну колонку можна перепризначити.</span>
        <span className="field-hint"><b>Імпорт</b> — лише колонка одразу після коду УКТ ЗЕД: «1» — товар ввезено на митну територію України
          (у ПН — ознака імпортованого товару), порожньо або «0» — ні. Наприклад, «Кабель ВВГ 3×2,5 · 8544499300 · 1 · м · 120,5 · 48,00» —
          кабель імпортний. Для ввезеного товару потрібен повний код УКТ ЗЕД — 10 цифр.</span>
        <textarea rows={5} value={text} autoFocus onChange={(e) => { setText(e.target.value); setOver(null); setHeader(null); }}
          placeholder={"Кабель ВВГ 3×2,5\t8544499300\t1\tм\t120,5\t48,00"} />
      </label>
      {lines.length > 0 && (<>
        <label className="check"><input type="checkbox" checked={map.headerLine != null} onChange={(e) => setHeader(e.target.checked)} />Перший рядок — назви колонок</label>
        <div className="table-wrap pn-paste-preview">
          <table>
            <caption className="sr-only">Попередній перегляд вставлених рядків</caption>
            <thead><tr><th scope="col">Рядок</th>
              {map.mapping.map((k, i) => (
                <th key={i} scope="col"><label className="sr-only" htmlFor={`pn-paste-col-${i}`}>Колонка {i + 1}</label>
                  <select id={`pn-paste-col-${i}`} value={k} onChange={(e) => setCol(i, e.target.value)}>
                    {Object.entries(PASTE_FIELDS).filter(([f]) => f !== "imported" || importAllowed(map.mapping, i)).map(([f, l]) => <option key={f} value={f}>{l}</option>)}
                  </select></th>))}
              <th scope="col">Стан</th></tr></thead>
            <tbody>
              {lines.filter((l) => l.cells.some(Boolean)).map((l) => {
                const r = res.rows.find((x) => x.line === l.line), sk = res.skipped.find((x) => x.line === l.line);
                return (
                  <tr key={l.line} className={sk ? "pn-paste-skip" : r?.problems.length ? "pn-paste-bad" : ""}>
                    <td>{l.line}</td>
                    {map.mapping.map((k, i) => <td key={i} className={k === "skip" ? "muted" : ""}>{l.cells[i] || ""}</td>)}
                    <td>{sk ? `пропущено: ${sk.reason}` : r?.problems.length ? r.problems.join("; ") : r?.rateFrom === "default" ? "✓ ставка з вибору нижче" : "✓"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!hasName && <p className="error small" role="alert">Позначте колонку з назвою товару чи послуги.</p>}
        {note && <p className="notice small" role="status">{note}.</p>}
        {noRate > 0 && (
          <label className="pn-bulk-rate">Ставка для {noRate} рядк. без ставки в таблиці
            <select value={defRate} onChange={(e) => setDefRate(e.target.value)}>
              <option value="">Оберу в рядках сам</option>
              {Object.entries(RATES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </label>
        )}
        <p className="small" role="status">
          Буде додано рядків: <b>{res.rows.length}</b>{bad ? `, з них з помилками: ${bad} (виправите у формі)` : ""}.
          {res.skipped.length > 0 && ` Пропущено: ${res.skipped.length} — ${res.skipped.map((x) => `рядок ${x.line} (${x.reason})`).join(", ")}.`}
          {res.empty > 0 && ` Порожніх рядків: ${res.empty}.`}
        </p>
      </>)}
      <div className="actions">
        <button type="button" className="btn btn-sm" disabled={!res.rows.length || !hasName} onClick={() => onDone(res.rows.map((x) => x.row), bad)}>
          Додати рядків: {res.rows.length}</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>Скасувати</button>
      </div>
    </div>
  );
}
